import * as THREE from "three";
import { Kz } from "@/physics/wind";
import { type WindState, gustSpeed } from "@/physics/gustModel";

/**
 * Wind visualization: a field of streaming streaks around the tower that shows
 * the airflow the physics actually sees — so "wind" stops being an invisible
 * number on a slider. It renders, from the same inputs the solver uses:
 *
 *   - DIRECTION — streaks fly along `windDir` (matches the solver's drag vector).
 *   - HEIGHT SHEAR — wind is faster aloft; streak speed/length follow the TIA
 *     velocity profile √Kz(z) (docs/engineering/01, 15), so you can see the
 *     boundary layer thicken toward the ground.
 *   - GUSTS — the whole field breathes with the gust model `gustSpeed()` when
 *     gusts are enabled (docs/engineering/02); a gust front visibly speeds the
 *     streaks up, the same instant the tower gets buffeted harder.
 *   - TURBULENCE / EDDIES — a curl-ish noise swirls the streaks; intensity grows
 *     with the turbulence intensity Iu (gustiness).
 *   - WAKE + VORTEX SHEDDING — downstream of the tower the mean flow drops into a
 *     velocity-deficit wake with alternating von Kármán vortices peeling off at
 *     the Strouhal frequency f = St·V/D (docs/engineering/03) — the very
 *     mechanism behind vortex-induced vibration.
 *
 * Purely a renderer: it reads wind params, never touches the solve. Streaks are
 * one THREE.LineSegments (2 verts/streak), advected on the CPU and recycled on
 * the upwind face — cheap enough for a couple thousand streaks.
 */

const STREAK_COUNT = 1600;
const ST_STROUHAL = 0.2; // Strouhal number for a bluff tower section

/** Deterministic RNG (seeded) so the field is reproducible across reloads. */
function makeRng(seed: number): () => number {
  let n = seed;
  return () => {
    n += 1;
    const s = Math.sin(n * 12.9898 + 78.233) * 43758.5453;
    return s - Math.floor(s);
  };
}

export interface WindParams {
  windSpeed: number; // mean/dial speed (m/s)
  windDir: number; // radians, direction the wind blows toward
  gustiness: number; // turbulence intensity Iu (0 = steady)
  windSeed: number; // gust trace seed
}

export class WindView {
  private scene: THREE.Scene;
  private geom: THREE.BufferGeometry;
  private lines: THREE.LineSegments;
  private positions: Float32Array;
  private colors: Float32Array;
  private rng = makeRng(9161);

  // per-streak head position + a small per-streak jitter phase
  private px: Float32Array;
  private py: Float32Array;
  private pz: Float32Array;

  private time = 0;
  private visible = true;

  // field geometry (set per tower)
  private R = 140; // horizontal half-extent (m)
  private topY = 120; // field height (m)
  private towerH = 60;
  private towerW = 4;

  // scratch
  private d = new THREE.Vector3(1, 0, 0); // downwind unit
  private left = new THREE.Vector3(0, 0, 1); // cross-wind unit (horizontal)
  private vel = new THREE.Vector3();

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    const n = STREAK_COUNT;
    this.positions = new Float32Array(n * 2 * 3);
    this.colors = new Float32Array(n * 2 * 3);
    this.px = new Float32Array(n);
    this.py = new Float32Array(n);
    this.pz = new Float32Array(n);

    this.geom = new THREE.BufferGeometry();
    this.geom.setAttribute("position", new THREE.BufferAttribute(this.positions, 3));
    this.geom.setAttribute("color", new THREE.BufferAttribute(this.colors, 3));

    const mat = new THREE.LineBasicMaterial({
      vertexColors: true,
      transparent: true,
      opacity: 0.7,
      depthWrite: false, // read as airy overlay; don't occlude the tower
      fog: true,
    });
    this.lines = new THREE.LineSegments(this.geom, mat);
    this.lines.renderOrder = 4;
    this.lines.frustumCulled = false;
    this.scene.add(this.lines);

    for (let i = 0; i < n; i++) this.seed(i, true);
  }

  /** Size the field to the current tower (call on rebuild). */
  setTower(height: number, halfWidth: number): void {
    this.towerH = Math.max(6, height);
    this.towerW = Math.max(1, halfWidth * 2);
    this.R = Math.max(this.towerH * 1.7, halfWidth * 9, 110);
    this.topY = this.towerH * 1.25 + 20;
    for (let i = 0; i < STREAK_COUNT; i++) this.seed(i, true);
  }

  setVisible(v: boolean): void {
    this.visible = v;
    this.lines.visible = v;
  }

  /** (Re)seed streak i. `anywhere` scatters it through the volume (init); else it
   *  respawns on the upwind face so the field keeps flowing without gaps. */
  private seed(i: number, anywhere: boolean): void {
    const along = anywhere ? (this.rng() * 2 - 1) * this.R : -this.R;
    const cross = (this.rng() * 2 - 1) * this.R;
    const y = 1 + this.rng() * (this.topY - 1);
    // place relative to the (current) wind axes
    this.px[i] = this.d.x * along + this.left.x * cross;
    this.pz[i] = this.d.z * along + this.left.z * cross;
    this.py[i] = y;
  }

  /** Wind velocity (m/s) at a world point — mean shear + gust + turbulence + wake. */
  private sampleWind(x: number, y: number, z: number, gustMul: number, turbAmp: number, V: number): THREE.Vector3 {
    const yc = Math.min(Math.max(y, 2), this.topY);
    const prof = Math.sqrt(Kz(yc) / Kz(10)); // height shear (√Kz)
    const meanMag = V * prof * gustMul;
    const v = this.vel.copy(this.d).multiplyScalar(meanMag);

    // turbulence: cheap curl-ish swirl from offset sines, vertical component small
    const t = this.time;
    const n1 = Math.sin(x * 0.15 + t * 1.1) + Math.sin(y * 0.22 - t * 0.8);
    const n2 = Math.sin(z * 0.17 - t * 1.3) + Math.sin(y * 0.19 + t * 0.9);
    const n3 = Math.sin(x * 0.12 - t * 0.7) + Math.sin(z * 0.14 + t * 1.05);
    const ta = turbAmp * meanMag;
    v.x += n1 * ta;
    v.z += n3 * ta;
    v.y += n2 * ta * 0.4;

    // wake + von Kármán vortex street, downstream of the tower (at origin)
    if (V > 3) {
      const along = x * this.d.x + z * this.d.z; // distance along wind from tower
      const cross = x * this.left.x + z * this.left.z; // cross-wind offset
      if (along > 0 && y < this.towerH * 1.05) {
        const D = Math.max(this.towerW, 2);
        const core = Math.exp(-(cross * cross) / (2 * (D * 0.7) * (D * 0.7)));
        const decay = Math.exp(-along / (D * 9));
        if (core > 0.02 && decay > 0.02) {
          // velocity deficit in the wake core
          v.addScaledVector(this.d, -meanMag * 0.55 * core * decay);
          // alternating cross-flow: von Kármán street at the Strouhal frequency
          const fShed = (ST_STROUHAL * Math.max(V, 3)) / D;
          const phase = 2 * Math.PI * fShed * t - along * 0.25;
          const cross2 = Math.sin(phase) * meanMag * 0.6 * core * decay;
          v.addScaledVector(this.left, cross2);
        }
      }
    }
    return v;
  }

  update(dt: number, p: WindParams): void {
    // Hide entirely in dead calm — nothing to show.
    const V = p.windSpeed;
    if (!this.visible || V < 1) {
      if (this.lines.visible !== (this.visible && V >= 1)) this.lines.visible = this.visible && V >= 1;
      return;
    }
    this.lines.visible = true;
    const step = Math.min(dt, 1 / 30);
    this.time += step;

    // wind axes
    this.d.set(Math.cos(p.windDir), 0, Math.sin(p.windDir));
    this.left.set(-this.d.z, 0, this.d.x);

    // temporal gust factor (matches the physics gust when gusts are enabled)
    let gustMul = 1;
    if (p.gustiness > 0) {
      const ws: WindState = { meanSpeed: V, gustiness: p.gustiness, seed: p.windSeed };
      gustMul = gustSpeed(ws, this.time) / Math.max(V, 0.001);
    }
    // turbulence amplitude: always a little (real air is never truly smooth),
    // more when the turbulence intensity (gustiness) is higher.
    const turbAmp = 0.08 + p.gustiness * 1.1;

    const pos = this.positions;
    const col = this.colors;
    const streakScale = this.R * 0.05; // streak length scales with the field size
    const invMaxSpeed = 1 / Math.max(V * 1.6, 12); // colour normalisation

    for (let i = 0; i < STREAK_COUNT; i++) {
      let x = this.px[i]!;
      let y = this.py[i]!;
      let z = this.pz[i]!;

      const v = this.sampleWind(x, y, z, gustMul, turbAmp, V);
      const speed = v.length();

      // advect the head
      x += v.x * step;
      y += v.y * step;
      z += v.z * step;

      // recycle when it leaves the box (along/cross/height bounds), else write back
      const along = x * this.d.x + z * this.d.z;
      const cross = x * this.left.x + z * this.left.z;
      if (along > this.R || along < -this.R * 1.05 || Math.abs(cross) > this.R || y < 0.5 || y > this.topY) {
        this.seed(i, false);
        x = this.px[i]!;
        y = this.py[i]!;
        z = this.pz[i]!;
      } else {
        this.px[i] = x;
        this.py[i] = y;
        this.pz[i] = z;
      }

      // streak: head at (x,y,z), tail trailing upwind along the local velocity
      const inv = speed > 1e-4 ? 1 / speed : 0;
      const len = (0.35 + 0.65 * Math.min(speed * invMaxSpeed, 1)) * streakScale;
      const tx = x - v.x * inv * len;
      const ty = y - v.y * inv * len;
      const tz = z - v.z * inv * len;

      const b = i * 6;
      pos[b] = x; pos[b + 1] = y; pos[b + 2] = z;
      pos[b + 3] = tx; pos[b + 4] = ty; pos[b + 5] = tz;

      // colour by speed: calm blue → white → gusty amber (head bright, tail faded)
      const f = Math.min(speed * invMaxSpeed, 1);
      let r: number, g: number, bl: number;
      if (f < 0.5) {
        const u = f / 0.5;
        r = 0.55 + 0.35 * u; g = 0.72 + 0.2 * u; bl = 0.95 + 0.05 * u;
      } else {
        const u = (f - 0.5) / 0.5;
        r = 0.9 + 0.1 * u; g = 0.92 - 0.32 * u; bl = 1.0 - 0.65 * u;
      }
      col[b] = r; col[b + 1] = g; col[b + 2] = bl;
      // tail dimmer for a comet-streak look
      col[b + 3] = r * 0.25; col[b + 4] = g * 0.25; col[b + 5] = bl * 0.25;
    }

    (this.geom.getAttribute("position") as THREE.BufferAttribute).needsUpdate = true;
    (this.geom.getAttribute("color") as THREE.BufferAttribute).needsUpdate = true;
  }

  dispose(): void {
    this.scene.remove(this.lines);
    this.geom.dispose();
    (this.lines.material as THREE.Material).dispose();
  }
}
